import { useMemo } from 'react';
import { Background, Controls, Handle, Position, ReactFlow, type NodeProps } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { AlertTriangle, Lock } from 'lucide-react';
import type { plans } from '@/lib/client';
import { layoutPlan, type StepNode } from '@/lib/dagLayout';
import { REVERT_NODE_STYLES, REVERT_STATUS_TEXT, STEP_STATUS_STYLES } from '@/lib/plans';
import { cn } from '@/lib/utils';
import { usePlansStore } from '@/stores/plansStore';
import { ChannelBadge } from './badges';

function StepNodeView({ data }: NodeProps<StepNode>) {
  const { step, selected } = data;
  const status = step.run?.status ?? 'pending';
  // Once the run reverted, a completed step is coloured by what became of its change (D14).
  const revert = step.run?.revert_status;
  return (
    <div
      className={cn(
        'h-[84px] w-[240px] rounded-lg border-2 bg-card px-3 py-2 text-left shadow-sm transition-shadow',
        revert ? REVERT_NODE_STYLES[revert] : STEP_STATUS_STYLES[status],
        selected && 'ring-2 ring-primary',
      )}
    >
      <Handle type="target" position={Position.Top} className="!h-2 !w-2 !bg-muted-foreground" />
      <div className="flex items-center justify-between gap-2">
        <ChannelBadge channel={step.channel} />
        <div className="flex items-center gap-1">
          {step.missing_inputs.length > 0 && <AlertTriangle className="h-3.5 w-3.5 text-amber-400" aria-label={`${step.missing_inputs.length} inputs missing`} />}
          {step.missing_scopes.length > 0 && <Lock className="h-3.5 w-3.5 text-amber-400" aria-label="scopes missing" />}
          {status === 'running' && <span className="h-2 w-2 animate-pulse rounded-full bg-blue-400" />}
          {status === 'waiting_human' && <span className="text-[10px] font-medium text-orange-400">your turn</span>}
          {revert && <span className={cn('text-[10px] font-medium', revert === 'failed' ? 'text-red-400' : 'text-orange-400')}>{REVERT_STATUS_TEXT[revert]}</span>}
        </div>
      </div>
      <div className="mt-1 line-clamp-2 text-sm font-medium leading-tight text-foreground">{step.title}</div>
      <Handle type="source" position={Position.Bottom} className="!h-2 !w-2 !bg-muted-foreground" />
    </div>
  );
}

const nodeTypes = { step: StepNodeView };

/** The plan as a graph (D8): steps as nodes with their channel and live status, dependencies as edges. */
export function PlanDag({ steps }: { steps: plans.PlanStepView[] }) {
  const selectedStepKey = usePlansStore((s) => s.selectedStepKey);
  const selectStep = usePlansStore((s) => s.selectStep);
  const { nodes, edges } = useMemo(() => layoutPlan(steps, selectedStepKey), [steps, selectedStepKey]);
  return (
    <div className="h-full min-h-[320px] w-full rounded-xl border border-border bg-background">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        fitView
        fitViewOptions={{ padding: 0.15, maxZoom: 1.2 }}
        minZoom={0.3}
        colorMode="dark"
        proOptions={{ hideAttribution: true }}
        onNodeClick={(_event, node) => selectStep(node.id)}
        onPaneClick={() => selectStep(null)}
      >
        <Background gap={16} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}
